import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const FLIGHT_FIELDS = [
  ["airline", "Airline"], ["flight_number", "Flight #"], ["confirmation_number", "Confirmation"],
  ["departure_airport", "Departure airport"], ["departure_city", "Departure city"], ["departure_time", "Departure", "datetime"],
  ["departure_tz", "Departure tz (IANA)"],
  ["arrival_airport", "Arrival airport"], ["arrival_city", "Arrival city"], ["arrival_time", "Arrival", "datetime"],
  ["arrival_tz", "Arrival tz (IANA)"],
  ["seat", "Seat"], ["cabin", "Cabin"], ["passenger_name", "Passenger"],
];

const HOTEL_FIELDS = [
  ["hotel_name", "Hotel name"], ["confirmation_number", "Confirmation"],
  ["check_in", "Check-in", "datetime"], ["check_out", "Check-out", "datetime"], ["tz", "Timezone (IANA)"],
  ["address", "Address"], ["city", "City"], ["country", "Country"],
  ["room_type", "Room type"], ["guest_name", "Guest"], ["phone", "Phone"],
];

const CAR_FIELDS = [
  ["vendor", "Vendor"], ["confirmation_number", "Confirmation"], ["vehicle_class", "Vehicle class"],
  ["pickup_location", "Pickup location"], ["pickup_address", "Pickup address"],
  ["pickup_time", "Pickup", "datetime"], ["pickup_tz", "Pickup tz (IANA)"],
  ["dropoff_location", "Dropoff location"], ["dropoff_address", "Dropoff address"],
  ["dropoff_time", "Dropoff", "datetime"], ["dropoff_tz", "Dropoff tz (IANA)"],
  ["driver_name", "Driver"], ["phone", "Phone"],
];

const EVENT_FIELDS = [
  ["event_name", "Event name"], ["event_type", "Event type"], ["confirmation_number", "Confirmation"],
  ["venue_name", "Venue"], ["address", "Address"], ["city", "City"], ["country", "Country"],
  ["start_time", "Start", "datetime"], ["end_time", "End", "datetime"], ["tz", "Timezone (IANA)"],
  ["seat", "Seat"], ["attendee_name", "Attendee"], ["phone", "Phone"],
];

export const TYPE_FIELDS = {
  flight: FLIGHT_FIELDS,
  hotel: HOTEL_FIELDS,
  car: CAR_FIELDS,
  event: EVENT_FIELDS,
};

export function toLocalInput(v) {
  if (!v) return "";
  const s = String(v);
  const dt = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (dt) return `${dt[1]}T${dt[2]}`;
  const d = s.match(/^(\d{4}-\d{2}-\d{2})$/);
  return d ? `${d[1]}T00:00` : "";
}

export function ReservationField({ field, value, onChange, prefix }) {
  const [key, label, kind] = field;
  return (
    <div>
      <label className="label-tag block mb-1">{label}</label>
      {kind === "datetime" ? (
        <input
          type="datetime-local"
          className="swiss-input text-sm"
          value={toLocalInput(value)}
          onChange={(e) => onChange(e.target.value || null)}
          data-testid={`${prefix}-${key}`}
        />
      ) : (
        <input
          className="swiss-input text-sm"
          value={value || ""}
          onChange={(e) => onChange(e.target.value || null)}
          data-testid={`${prefix}-${key}`}
        />
      )}
    </div>
  );
}

export default function EditReservationDialog({ reservation, tripId, onClose, onSaved }) {
  const t = reservation.type;
  const initial = reservation[t] || {};
  const [form, setForm] = useState({ ...initial });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const body = { [t]: form };
      await api.patch(`/trips/${tripId}/reservations/${reservation.id}`, body);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const fields = TYPE_FIELDS[t] || [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-none border-sand-300" data-testid="edit-reservation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-medium text-2xl tracking-tight">
            Edit {t}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-auto pr-2 py-2">
          {fields.map((f) => (
            <ReservationField
              key={f[0]}
              field={f}
              value={form[f[0]]}
              onChange={(v) => setForm({ ...form, [f[0]]: v })}
              prefix="edit"
            />
          ))}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-swiss btn-ghost text-sm" data-testid="edit-cancel">
            Cancel
          </button>
          <button onClick={save} disabled={busy} className="btn-swiss btn-primary text-sm" data-testid="edit-save">
            {busy ? "Saving..." : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
